import * as React from "react";
import styled from 'styled-components';
import Button from 'src/SharedComponents/Button';
import ViewContainer from 'src/SharedComponents/ViewContainer';
import ManagerTitleBar from 'src/Views/Manager/ManagerTitleBar';
import RoleUserDetails from "./RoleUserDetails";
import EditRoleDialog from "src/Views/Ram/EditRoleDialog";
import createErrorMessage from 'src/UsefulFunctions/createErrorMessage';
import * as roleManagementService from "./roleManagementService";
import IRole from 'src/ServerEntities/IRole';

const ButtonContainer = styled.div`
    display: flex;
    gap: 8px;
`;

interface IRoleDetailsProps {
    selectedRole: IRole;
    webToken: string;
    refreshRoles: () => void;
    setErrorMessage: (errorMessage: string) => void;
    currentRole: string;
    currentUser: string;
}

const RoleDetails = (props: IRoleDetailsProps) => {
    const { webToken, selectedRole, currentRole, refreshRoles, setErrorMessage, currentUser } = props;
    
    const [refresh, setRefresh] = React.useState(0);
    const [showEditDialog, setShowEditDialog] = React.useState(false);
    const [editLoading, setEditLoading] = React.useState(false);
    const [editError, setEditError] = React.useState("");
    const [editSuccess, setEditSuccess] = React.useState(false);

    const refreshHistory = () => {
        setRefresh(refresh + 1);
    };

    const disableRole = () => {
        if (selectedRole) {
            setErrorMessage("");
            roleManagementService.disableRole(
                selectedRole.id,
                webToken,
                () => { 
                    refreshRoles();
                    refreshHistory();
                },
                (serverError: string) => {
                    setErrorMessage(createErrorMessage("disabling role", serverError));
                }
            );
        }
    };

    const enableRole = () => {
        if (selectedRole) {
            setErrorMessage("");
            roleManagementService.enableRole(
                selectedRole.id,
                webToken,
                () => { 
                    refreshRoles();
                    refreshHistory();
                },
                (serverError: string) => {
                    setErrorMessage(createErrorMessage("enabling role", serverError));
                }
            );
        }
    };

    const openEditDialog = () => {
        setShowEditDialog(true);
        setEditError("");
        setEditSuccess(false);
    };

    const closeEditDialog = () => {
        setShowEditDialog(false);
        setEditError("");
        setEditSuccess(false);
    };

    const handleSaveRole = (displayName: string, description: string) => {
        setEditLoading(true);
        setEditError("");
        roleManagementService.updateRole(
            selectedRole.id,
            displayName,
            description,
            webToken,
            () => {
                setEditLoading(false);
                setEditSuccess(true);
                refreshRoles();
                refreshHistory();
            },
            (serverError: string) => {
                setEditLoading(false);
                setEditError(createErrorMessage("updating role", serverError));
            }
        );
    };
   
    return (
        <ViewContainer>
            <EditRoleDialog 
                role={selectedRole}
                show={showEditDialog}
                onClose={closeEditDialog}
                onSave={handleSaveRole}
                loading={editLoading}
                errorMessage={editError}
                success={editSuccess}
            />
            
            <ManagerTitleBar viewName={`${selectedRole.displayName || selectedRole.id}`} viewDescription={`${selectedRole.description}`}>
                <ButtonContainer>
                    <Button onClick={openEditDialog}>Edit role</Button>
                    {!selectedRole.status && (
                        <Button color={"#4f7326"} onClick={enableRole} disabled={selectedRole && selectedRole.id === currentRole}>
                            Enable role
                        </Button>
                    )}
                    {selectedRole.status && (
                        <Button color={"#ad0000"} onClick={disableRole} disabled={selectedRole && selectedRole.id === currentRole}>
                            Disable role
                        </Button>
                    )}
                </ButtonContainer>
            </ManagerTitleBar>
            
            <RoleUserDetails
                selectedRole={selectedRole}
                webToken={webToken}
                currentUser={currentUser}
                currentRole={currentRole}
                setErrorMessage={setErrorMessage}
            />
        </ViewContainer>
    );
};

export default RoleDetails;